import { useState, useEffect } from "react";
import { Dialog, DialogPanel, DialogBackdrop } from "@headlessui/react";
import {
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { Button } from "../Button";
import { APIs } from "../../services/Apis";

const STATUS_CONFIG = {
  "Goal Achieved": {
    label: "Goal Achieved",
    tooltip: "Users who completed the goal after going through the actions",
    dot: "bg-emerald-500",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    countBg: "bg-emerald-50",
    countBorder: "border-emerald-200",
    countText: "text-emerald-700",
    countSubText: "text-emerald-600",
  },
  "In Progress": {
    label: "In Progress",
    tooltip:
      "Users currently going through the actions who haven't converted yet",
    dot: "bg-amber-500",
    bg: "bg-amber-50",
    text: "text-amber-700",
    countBg: "bg-amber-50",
    countBorder: "border-amber-200",
    countText: "text-amber-700",
    countSubText: "text-amber-600",
  },
  "Completed No Conversion": {
    label: "Completed No Conversion",
    tooltip: "Users who went through all the actions without converting",
    dot: "bg-red-500",
    bg: "bg-red-50",
    text: "text-red-700",
    countBg: "bg-red-50",
    countBorder: "border-red-200",
    countText: "text-red-700",
    countSubText: "text-red-600",
  },
  "Campaign Paused": {
    label: "Campaign Paused",
    tooltip: "Users whose campaign is currently paused",
    dot: "bg-slate-400",
    bg: "bg-slate-50",
    text: "text-slate-600",
    countBg: "bg-slate-50",
    countBorder: "border-slate-200",
    countText: "text-slate-600",
    countSubText: "text-slate-500",
  },
  "Not Started": {
    label: "Not Started",
    tooltip: "Users who have been added but haven't received any actions yet",
    dot: "bg-gray-400",
    bg: "bg-gray-50",
    text: "text-gray-600",
    countBg: "bg-gray-50",
    countBorder: "border-gray-200",
    countText: "text-gray-600",
    countSubText: "text-gray-500",
  },
};

const STATUS_DISPLAY_ORDER = [
  "Not Started",
  "Campaign Paused",
  "In Progress",
  "Goal Achieved",
  "Completed No Conversion",
];

function StatusCard({ statusKey, count }) {
  const config = STATUS_CONFIG[statusKey];

  return (
    <div
      className={`${config.countBg} border ${config.countBorder} rounded-xl p-4 flex flex-col justify-between h-24`}
    >
      <div className="flex items-center gap-1.5">
        <p className={`text-xs font-medium ${config.countSubText}`}>
          {config.label}
        </p>
        <div className="relative group">
          <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400 cursor-help" />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg w-48 text-center opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-10">
            {config.tooltip}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
          </div>
        </div>
      </div>
      <p className={`text-3xl font-bold ${config.countText}`}>{count}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG["Not Started"];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
}

function AnalyticsLoader() {
  return (
    <div className="animate-pulse space-y-6 p-6">
      <div className="bg-gray-100 rounded-xl h-16" />
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-100 rounded-xl h-14" />
        ))}
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ReceivedActionPill({ action }) {
  return (
    <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1 text-xs">
      <span className="font-medium text-gray-700 truncate max-w-[160px]">
        {action.emailSubject || `Action #${action.segmentActionId}`}
      </span>
      <span className="text-gray-400">·</span>
      <span className="text-gray-400 whitespace-nowrap">
        {formatDate(action.sentAt)}
      </span>
    </div>
  );
}

function ExpandedRow({ user }) {
  if (!user.receivedActions?.length) return null;

  return (
    <tr>
      <td colSpan={4} className="px-5 pb-4 pt-0">
        <div className="flex flex-wrap gap-2">
          {user.receivedActions.map((action, idx) => (
            <ReceivedActionPill key={idx} action={action} />
          ))}
        </div>
      </td>
    </tr>
  );
}

function UserTableRow({ user, isExpanded, onToggle }) {
  return (
    <>
      <tr
        className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="py-3 pl-5 pr-3">
          <p className="text-sm font-medium text-gray-900 truncate" title={user.name}>{user.name}</p>
        </td>
        <td className="py-3 px-3">
          <p className="text-sm text-gray-500 truncate" title={user.email}>{user.email}</p>
        </td>
        <td className="py-3 px-3 text-center">
          <span className="text-xs text-gray-500 tabular-nums">
            {user.receivedActions?.length || 0}
          </span>
        </td>
        <td className="py-3 pl-3 pr-5 w-44">
          <StatusBadge status={user.status} />
        </td>
      </tr>
      {isExpanded && <ExpandedRow user={user} />}
    </>
  );
}

export const ActionAnalyticsPanel = ({ isOpen, onClose, segmentId }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedEmail, setExpandedEmail] = useState(null);

  useEffect(() => {
    if (!isOpen || !segmentId) return;

    setLoading(true);
    setError(null);

    APIs.getSegmentAnalytics(segmentId)
      .then((response) => {
        if (response?.success) {
          setAnalytics(response.analytics || []);
        } else {
          setError(response?.message || "Failed to load analytics");
        }
      })
      .catch((err) => {
        console.error("Error fetching analytics:", err);
        setError("An error occurred while loading analytics");
      })
      .finally(() => setLoading(false));
  }, [isOpen, segmentId]);

  const statusCounts = analytics.reduce((acc, user) => {
    const status = user.status;
    if (status) acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const query = searchQuery.toLowerCase();
  const filteredAnalytics = analytics.filter(
    (user) =>
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query),
  );

  function handleToggleExpand(email) {
    setExpandedEmail((prev) => (prev === email ? null : email));
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/25 transition-opacity duration-300 ease-in-out data-closed:opacity-0"
      />
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-4xl transform transition duration-300 ease-in-out data-closed:translate-x-full"
            >
              <div className="flex flex-col h-full bg-white shadow-2xl overflow-hidden border-l border-gray-200">
                <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Segment Analytics
                  </h2>
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Close
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loading && <AnalyticsLoader />}

                  {!loading && error && (
                    <div className="flex items-center justify-center py-16">
                      <p className="text-sm text-red-500">{error}</p>
                    </div>
                  )}

                  {!loading && !error && (
                    <div className="p-6 space-y-6">
                      <div className="grid grid-cols-5 gap-3">
                        {STATUS_DISPLAY_ORDER.map((statusKey) => (
                          <StatusCard
                            key={statusKey}
                            statusKey={statusKey}
                            count={statusCounts[statusKey] || 0}
                          />
                        ))}
                      </div>

                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between gap-4">
                          <h3 className="text-sm font-semibold text-gray-700 shrink-0">
                            Users ({filteredAnalytics.length})
                          </h3>
                          <div className="relative max-w-xs w-full">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search by name or email…"
                              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                          </div>
                        </div>

                        {filteredAnalytics.length > 0 ? (
                          <>
                            <table className="w-full table-fixed">
                              <thead>
                                <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  <th className="py-2.5 pl-5 pr-3 text-left w-[30%]">
                                    Name
                                  </th>
                                  <th className="py-2.5 px-3 text-left w-[35%]">
                                    Email
                                  </th>
                                  <th className="py-2.5 px-3 text-center w-[10%]">
                                    Actions
                                  </th>
                                  <th className="py-2.5 pl-3 pr-5 text-left w-[25%]">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredAnalytics.map((user) => (
                                  <UserTableRow
                                    key={user.email}
                                    user={user}
                                    isExpanded={expandedEmail === user.email}
                                    onToggle={() =>
                                      handleToggleExpand(user.email)
                                    }
                                  />
                                ))}
                              </tbody>
                            </table>
                            <div className="pb-6" />
                          </>
                        ) : (
                          <div className="flex items-center justify-center py-12">
                            <p className="text-sm text-gray-400">
                              {analytics.length === 0
                                ? "No analytics data yet"
                                : "No users match your search"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
